import React, { useEffect, useRef, useState } from 'react';
import { testimonials } from '../data/mock';
import { Quote, ChevronLeft, ChevronRight } from 'lucide-react';

const Testimonials = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section
      id="testimonials"
      ref={sectionRef}
      className="bg-[#0d0b09] py-16 md:py-24 lg:py-32 px-4 md:px-6 overflow-hidden"
    >
      <div className="container mx-auto max-w-4xl">
        {/* Section Header */}
        <div
          className={`text-center mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Divider */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent to-[#8B7355]/30" />
            <div className="mx-4 w-2 h-2 rotate-45 border border-[#D4A853]" />
            <div className="flex-1 h-[1px] bg-gradient-to-l from-transparent to-[#8B7355]/30" />
          </div>

          <h2 className="text-[#A69080] text-2xl md:text-3xl tracking-[0.3em] uppercase mb-4">
            Client Testimonials
          </h2>

          {/* Divider */}
          <div className="flex items-center justify-center mt-8">
            <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent to-[#8B7355]/30" />
            <div className="mx-4 w-2 h-2 rotate-45 border border-[#6B7B4B]" />
            <div className="flex-1 h-[1px] bg-gradient-to-l from-transparent to-[#8B7355]/30" />
          </div>

          <p className="text-[#8B7355] mt-8 text-base md:text-lg">
            Hear from those who have experienced my services.
          </p>
        </div>

        {/* Testimonials Carousel */}
        <div
          className={`relative transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Quote Icon */}
          <div className="flex justify-center mb-8">
            <Quote className="w-12 h-12 text-[#D4A853]/30" />
          </div>

          {/* Testimonial Content */}
          <div className="relative min-h-[250px] md:min-h-[200px]">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                className={`absolute inset-0 transition-all duration-700 ${
                  index === currentIndex
                    ? 'opacity-100 translate-x-0'
                    : index < currentIndex
                    ? 'opacity-0 -translate-x-full'
                    : 'opacity-0 translate-x-full'
                }`}
              >
                <blockquote className="text-center">
                  <p className="text-[#A69080] text-lg md:text-xl leading-relaxed italic mb-8">
                    "{testimonial.text}"
                  </p>
                  <footer className="text-[#D4A853] text-sm tracking-[0.2em] uppercase">
                    — {testimonial.author}
                  </footer>
                </blockquote>
              </div>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-6 mt-12">
            <button
              onClick={prevTestimonial}
              className="p-3 border border-[#8B7355]/30 text-[#8B7355] hover:border-[#D4A853] hover:text-[#D4A853] transition-all duration-300"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {/* Indicators */}
            <div className="flex gap-3">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? 'bg-[#D4A853] w-6'
                      : 'bg-[#8B7355]/30 hover:bg-[#8B7355]'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={nextTestimonial}
              className="p-3 border border-[#8B7355]/30 text-[#8B7355] hover:border-[#D4A853] hover:text-[#D4A853] transition-all duration-300"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
